import { Done, ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Menu,
  MenuItem,
  ToggleButton,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useQueryClient } from "@tanstack/react-query";
import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import useAPI from "../../api/hooks/useAPI";
import useColors from "../../api/hooks/useColors";
import useMSSP from "../../api/hooks/useMSSP";
import {
  bootstrapKeys,
  entityKeys,
  inventoryBootstrapKeys,
  vulnerabilityKeys,
} from "../../api/queryKeys";
import { BootstrapContext } from "../../contexts/BootstrapContext";
import SvgToIcon from "../../icons/SvgToIcon";
import { icons } from "../../scenes/stack/environment/icons/iconExport";
import AddNewTenantButton from "../../scenes/tenants_management/AddNewTenantButton";
import AddNewTenantModal from "../../scenes/tenants_management/AddNewTenantModal";
import {
  ComplexEntityTypes,
  UserPlatformAccessRole,
} from "../../utils/constants";
import CustomToolTip from "../CustomTooltip";
import SearchField from "../filters/SearchField";
import LoadingIcon from "../LoadingIcon";
import { Toast } from "../Toast";
import TenantAvatar from "./TenantAvatar";

const MSSPSelectTenantButton = ({ isAllTenants, setIsAllTenants }) => {
  const queryClient = useQueryClient();
  const colors = useColors();
  const { setMSSPTenantIsPinned, trackEvent } = useAPI();
  const theme = useTheme();
  const {
    isMSSP,
    msspId,
    currentTenantId,
    switchTenant,
    selectedTenantIds,
    setSelectedTenantIds,
    hasMultipleSelectedTenants,
  } = useMSSP();

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [hoveredPin, setHoveredPin] = useState(null);
  const [isAddTenantModalOpen, setIsAddTenantModalOpen] = useState(false);
  const [initialSelectedTenantIds, setInitialSelectedTenantIds] = useState([]);
  const open = Boolean(anchorEl);
  const location = useLocation();

  const isMultiTenantPage =
    location.pathname.includes("environment") ||
    location.pathname.includes("vulnerabilities");

  const isEnvironmentPage = location.pathname.includes("environment");
  const isVulnerabilitiesPage = location.pathname.includes("vulnerabilities");

  const { msspTenants: rawTenants, user: platformUser } =
    useContext(BootstrapContext);

  const tenants = useMemo(() => {
    if (!rawTenants?.length) return [];
    return [...rawTenants].sort((a, b) => {
      if (a.customer_id === currentTenantId) return -1;
      if (b.customer_id === currentTenantId) return 1;
      if (a.customer_id === msspId) return -1;
      if (b.customer_id === msspId) return 1;
      if (a.is_pinned && !b.is_pinned) return -1;
      if (!a.is_pinned && b.is_pinned) return 1;
      const nameA = a.company_name?.toLowerCase() ?? a.customer_id ?? "";
      const nameB = b.company_name?.toLowerCase() ?? b.customer_id ?? "";
      return nameA.localeCompare(nameB);
    });
  }, [rawTenants, msspId, currentTenantId]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setInitialSelectedTenantIds([...selectedTenantIds]);
  };

  const handleClose = () => {
    setAnchorEl(null);

    const hasChanged =
      initialSelectedTenantIds.length !== selectedTenantIds.length ||
      !initialSelectedTenantIds.every((id) => selectedTenantIds.includes(id));

    if (hasChanged) {
      if (isEnvironmentPage) {
        // clearRouterCache().then(() => {
        queryClient.invalidateQueries({
          queryKey: entityKeys.entitiesOverview(),
        });
        queryClient.invalidateQueries({
          queryKey: inventoryBootstrapKeys.all,
        });
        queryClient.invalidateQueries({
          queryKey: entityKeys.allEntities(),
        });

        Object.values(ComplexEntityTypes).forEach((entityType) => {
          queryClient.invalidateQueries({
            queryKey: entityKeys.entitiesByType(entityType),
          });
        });
      }
      if (isVulnerabilitiesPage) {
        queryClient.invalidateQueries({
          queryKey: vulnerabilityKeys.all,
        });
        // Invalidate all entities by vulnerability (from vulnerability drawers)
        queryClient.invalidateQueries({
          queryKey: entityKeys.allEntitiesByVulnerability(),
        });
      }
      // });
    }
  };

  // Switch tenant
  const handleMenuItemClick = async (tenant) => {
    try {
      switchTenant(tenant.customer_id);
      setSelectedTenant(tenant);
      // Add the tenant to the selected tenant ids if it is not already selected
      let newSelectedTenantIds = [...selectedTenantIds];
      if (!newSelectedTenantIds.includes(tenant.customer_id)) {
        newSelectedTenantIds.push(tenant.customer_id);
      }
      if (newSelectedTenantIds.includes(currentTenantId)) {
        newSelectedTenantIds = newSelectedTenantIds.filter(
          (id) => id !== currentTenantId
        );
      }
      setSelectedTenantIds(newSelectedTenantIds);
      trackEvent(`User switched to tenant ${tenant.customer_id}`);
    } catch (error) {
      Toast.error(`Couldn't switch tenant, please contact the support team`);
    }
    handleClose();
  };

  const handlePinClick = async (tenant, event) => {
    // Prevent menu item click
    event.stopPropagation();

    try {
      await setMSSPTenantIsPinned(tenant.customer_id, !tenant.is_pinned);
      queryClient.invalidateQueries({
        queryKey: bootstrapKeys.all,
      });
      trackEvent(
        `User ${tenant.is_pinned ? "unpinned" : "pinned"} tenant ${
          tenant.customer_id
        }`
      );
    } catch (error) {
      Toast.error(`Couldn't pin tenant, please contact the support team`);
    }
  };

  useEffect(() => {
    if (isMSSP) {
      setSelectedTenant(
        tenants.find((tenant) => tenant.customer_id === currentTenantId)
      );
      // Add the current tenant to the selected tenant ids if it is not already selected
      if (!selectedTenantIds.includes(currentTenantId)) {
        setSelectedTenantIds([...selectedTenantIds, currentTenantId]);
      }
    }
  }, [isMSSP, currentTenantId, tenants, selectedTenantIds]);

  const availableTenants = tenants.filter(
    (tenant) => tenant.customer_id !== selectedTenant?.customer_id
  );

  const allTenantsSelected =
    availableTenants.length > 0 &&
    availableTenants.every((tenant) =>
      selectedTenantIds.includes(tenant.customer_id)
    );

  const handleCheckboxChange = (tenantId, isChecked) => {
    if (isChecked) {
      setSelectedTenantIds([...selectedTenantIds, tenantId]);
    } else {
      // If the tenant is the only selected tenant, do not remove it
      if (selectedTenantIds.length === 1 && selectedTenantIds[0] === tenantId) {
        return;
      }

      // Remove the tenant from the selected tenant ids
      const newSelectedTenantIds = selectedTenantIds.filter(
        (id) => id !== tenantId
      );
      setSelectedTenantIds(newSelectedTenantIds);
      // If the tenant is the current tenant, switch to the next tenant
      if (tenantId === currentTenantId) {
        const nextTenant = availableTenants.find(
          (tenant) => newSelectedTenantIds[0] === tenant.customer_id
        );
        // If the next tenant is found don't switch to it, just update the selected tenant ids
        if (nextTenant) {
          handleMenuItemClick(nextTenant);
        }
      }
    }

    trackEvent(
      `User ${
        isChecked ? "selected" : "unselected"
      } tenant ${tenantId} for multi-tenant view`
    );
  };

  const handleSelectAll = () => {
    const availableTenantIds = availableTenants.map(
      (tenant) => tenant.customer_id
    );

    if (allTenantsSelected) {
      setSelectedTenantIds([currentTenantId]);
      trackEvent("User deselected all tenants for multi-tenant view");
    } else {
      setSelectedTenantIds(availableTenantIds);
      trackEvent("User selected all tenants for multi-tenant view");
    }
  };

  return (
    <>
      <>
        <ToggleButton
          value="tenant"
          selected={!!selectedTenant && !isAllTenants}
          onClick={handleClick}
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "8px",
            borderRadius: "8px",
            border: "none",
            padding: "6px 8px",
            backgroundColor: colors.toggle_button_enabled_bg,
            "&:hover": {
              backgroundColor: colors.toggle_button_hovered_bg,
            },
            "&.Mui-selected": {
              backgroundColor: colors.toggle_button_selected_bg,
              color: colors.text_selected,
              "&:hover": {
                backgroundColor: colors.toggle_button_selected_bg,
              },
              "&.Mui-focusVisible": {
                backgroundColor: colors.toggle_button_focused_bg,
              },
            },
          }}
        >
          {selectedTenant ? (
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
              onClick={() => setIsAllTenants(false)}
            >
              <SvgToIcon IconLogo={icons.SingleTenantIcon} width="20px" />

              <Typography variant="overline">
                {selectedTenant?.company_name}
              </Typography>

              <SvgToIcon IconLogo={icons.chevronDownIcon} width="20px" />
            </Box>
          ) : (
            <LoadingIcon size={10} />
          )}
        </ToggleButton>
        {hasMultipleSelectedTenants && isMultiTenantPage && (
          <Accordion
            sx={{
              backgroundColor: "transparent",
              boxShadow: "none",
              "&:before": {
                display: "none",
              },
              "& .MuiAccordionSummary-root": {
                minHeight: "auto",
                padding: 0,
              },
              "& .MuiAccordionSummary-content": {
                margin: "8px 0",
              },
              "& .MuiAccordionDetails-root": {
                padding: "0 0 8px 0",
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore sx={{ fontSize: "20px" }} />}
              sx={{
                "& .MuiAccordionSummary-expandIconWrapper": {
                  color: colors.text_primary,
                },
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: colors.text_secondary }}
              >
                {selectedTenantIds.length} tenant
                {selectedTenantIds.length > 1 ? "s" : ""} selected
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                {selectedTenantIds.map((tenantId) => (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      backgroundColor: colors.toggle_button_hovered_bg,
                      border: `1px solid ${colors.divider}`,
                      padding: "4px 8px",
                      borderRadius: "8px",
                    }}
                    key={tenantId}
                  >
                    <Typography variant="caption">{tenantId}</Typography>
                  </Box>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}
      </>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            padding: "4px 12px",
            gap: "4px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              padding: "4px 4px",
            }}
          >
            <Typography variant="overline">Select a Tenant</Typography>
            <Typography variant="caption" sx={{ color: colors.text_secondary }}>
              Choose a tenant to view its environment
            </Typography>
          </Box>
          <SearchField
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            colors={colors}
            theme={theme}
          />

          {selectedTenant && (
            <MenuItem
              sx={{
                padding: "10px 4px",
                borderRadius: "8px",
                width: "100%",
                backgroundColor: colors.toggle_button_hovered_bg,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: "8px",
                  width: "100%",
                  alignItems: "center",
                  padding: "0 4px",
                }}
              >
                {isMultiTenantPage && (
                  <Checkbox
                    checked={selectedTenantIds.includes(
                      selectedTenant.customer_id
                    )}
                    onChange={(e) =>
                      handleCheckboxChange(
                        selectedTenant.customer_id,
                        e.target.checked
                      )
                    }
                    onClick={(e) => e.stopPropagation()}
                    sx={{ padding: 0, marginRight: "8px" }}
                    icon={
                      <SvgToIcon
                        IconLogo={icons.Unchecked}
                        style={{ width: "20px", height: "20px" }}
                      />
                    }
                    checkedIcon={
                      <SvgToIcon
                        IconLogo={icons.Checked}
                        style={{ width: "20px", height: "20px" }}
                      />
                    }
                  />
                )}
                <TenantAvatar tenant={selectedTenant} />

                <Typography variant="caption">
                  {selectedTenant?.company_name}
                </Typography>
                <Done
                  sx={{
                    marginLeft: "auto",
                    color: colors.text_primary,
                    width: "16px",
                  }}
                />
              </Box>
            </MenuItem>
          )}

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                ml: "4px",
                mt: "7px",
              }}
            >
              <Typography variant="overline">Tenants</Typography>
              {isMultiTenantPage && availableTenants.length > 0 && (
                <Typography
                  variant="caption"
                  onClick={handleSelectAll}
                  sx={{
                    cursor: "pointer",
                    color: colors.text_link,
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  {allTenantsSelected ? "Clear Selection" : "Select All"}
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                borderBottom: `1px solid ${colors.divider}`,
                maxHeight: 220, // 5 menu items × 40px = 200px. 20px for padding.
                overflowY: "auto",
                mb: "8px",
              }}
            >
              {tenants
                .filter(
                  (tenant) =>
                    tenant.company_name
                      .toLowerCase()
                      .includes(searchQuery.toLowerCase()) &&
                    tenant.customer_id !== selectedTenant?.customer_id
                )
                .map((tenant) => (
                  <MenuItem
                    key={tenant.customer_id}
                    sx={{
                      padding: "8px 4px",
                      borderRadius: "8px",
                      position: "relative",
                      "&:hover": {
                        "& .MuiBox-root > svg:last-child": {
                          visibility: "visible",
                        },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        gap: "8px",
                        padding: "0 4px",
                        width: "100%",
                      }}
                    >
                      {isMultiTenantPage && (
                        <Checkbox
                          checked={selectedTenantIds.includes(
                            tenant.customer_id
                          )}
                          onChange={(e) =>
                            handleCheckboxChange(
                              tenant.customer_id,
                              e.target.checked
                            )
                          }
                          onClick={(e) => e.stopPropagation()}
                          sx={{ padding: 0, marginRight: "8px" }}
                          icon={
                            <SvgToIcon
                              IconLogo={icons.Unchecked}
                              style={{ width: "20px", height: "20px" }}
                            />
                          }
                          checkedIcon={
                            <SvgToIcon
                              IconLogo={icons.Checked}
                              style={{ width: "20px", height: "20px" }}
                            />
                          }
                        />
                      )}
                      <TenantAvatar tenant={tenant} />
                      <Typography variant="caption">
                        {tenant.company_name}
                      </Typography>
                      <Box
                        onClick={(e) => handlePinClick(tenant, e)}
                        onMouseEnter={() => setHoveredPin(tenant.customer_id)}
                        onMouseLeave={() => setHoveredPin(null)}
                        sx={{
                          marginLeft: "auto",
                          cursor: "pointer",
                          "& > svg": {
                            visibility: tenant.is_pinned ? "visible" : "hidden",
                          },
                          "&:hover > svg": {
                            visibility: "visible",
                          },
                        }}
                      >
                        <SvgToIcon
                          IconLogo={
                            tenant.is_pinned ||
                            hoveredPin === tenant.customer_id
                              ? icons.pinSelectedIcon
                              : icons.pinDefaultIcon
                          }
                          width="14px"
                          style={{
                            fill: "none",
                          }}
                        />
                      </Box>
                      <CustomToolTip title="Switch Tenant" placement="top">
                        <Box
                          onClick={() => handleMenuItemClick(tenant)}
                          sx={{
                            cursor: "pointer",
                            visibility: "hidden",

                            "&:hover": {
                              visibility: "visible",
                            },
                          }}
                        >
                          <SvgToIcon
                            IconLogo={icons.arrowRightIcon}
                            width="16px"
                          />
                        </Box>
                      </CustomToolTip>
                    </Box>
                  </MenuItem>
                ))}
            </Box>

            <AddNewTenantButton
              onClick={() => {
                handleClose();
                setIsAddTenantModalOpen(true);
              }}
              disabled={
                platformUser?.access_role === UserPlatformAccessRole.MSSP_VIEWER
              }
            />
          </Box>
        </Box>
      </Menu>

      <AddNewTenantModal
        open={isAddTenantModalOpen}
        onClose={() => setIsAddTenantModalOpen(false)}
      />
    </>
  );
};

export default MSSPSelectTenantButton;
